import { FC } from 'react'
import styled, { css, keyframes } from 'styled-components'
import {
  Primary13Regular,
  RedRed60,
  GreyGrey30,
  IndigoIndigo50,
  IndigoIndigo10,
  Box,
  Flex
} from '@sequencehq/core-components'
import { Phase as PhaseType } from 'modules/Cube/domain/cube.domain.types'
import { usePhase } from 'Cube/view/common/widgets/Pricing/Phase/usePhase'
import { SearchForProduct } from 'Cube/view/common/widgets/Pricing/Phase/SearchForProduct/SearchForProduct'
import { PhaseHeader } from 'Cube/view/common/widgets/Pricing/Phase/PhaseHeader'
import { PhaseDurationSelection } from 'Cube/view/common/widgets/Pricing/Phase/PhaseDurationSelection'
import { PhaseActions } from 'Cube/view/common/widgets/Pricing/Phase/PhaseActions'
import { PhaseAlignmentWarning } from 'Cube/view/common/widgets/Pricing/Phase/PhaseAlignmentWarning'
import { PricingTableExpanded } from './PricingTable/PricingTableExpanded'
import { PricingTableCompact } from './PricingTable/compact/PricingTableCompact'
import { PHASE_INSERT_FLASH_MS } from 'Cube/view/common/widgets/Pricing/usePricingWidget'

const phaseInsertFlash = keyframes`
  0% { background-color: ${IndigoIndigo10}; }
  100% { background-color: transparent; }
`

const PhaseContainer = styled(Box)<{ $isNewlyInserted: boolean }>`
  animation: ${props =>
    props.$isNewlyInserted
      ? css`
          ${phaseInsertFlash} ${PHASE_INSERT_FLASH_MS}ms ease-out both
        `
      : 'none'};
`

export const Phase: FC<{
  phaseId: PhaseType['id']
  expanded: boolean
  onExpand: (expanded: boolean) => void
  focusDurationOnLoad?: boolean
  wideLayout?: boolean
  onInsertBefore?: () => void
  onInsertAfter?: () => void
  isNewlyInserted?: boolean
}> = props => {
  const phasesHook = usePhase({
    phaseId: props.phaseId,
    expanded: props.expanded,
    onExpand: props.onExpand,
    focusDurationOnLoad: props.focusDurationOnLoad
  })

  return (
    <PhaseContainer
      _notFirst={{
        borderTop: phasesHook.isHighlighted
          ? undefined
          : `1px solid ${GreyGrey30}`
      }}
      data-testid={`phase.section`}
      data-phaseid={props.phaseId}
      data-test-is-highlighted={phasesHook.isHighlighted}
      borderRadius={phasesHook.isHighlighted ? '8px' : undefined}
      border={
        phasesHook.isHighlighted ? `1px solid ${IndigoIndigo50}` : undefined
      }
      onClick={phasesHook.onClick}
      $isNewlyInserted={Boolean(props.isNewlyInserted)}
    >
      <Box padding="0 16px">
        <PhaseHeader
          phaseId={props.phaseId}
          expanded={phasesHook.expanded}
          onExpand={(newState: boolean) => phasesHook.updateExpanded(newState)}
          onInsertBefore={props.onInsertBefore}
          onInsertAfter={props.onInsertAfter}
        />
      </Box>
      {phasesHook.expanded && (
        <Box paddingBottom="16px">
          <Box padding="0 16px">
            <PhaseAlignmentWarning phaseId={props.phaseId} />
            <PhaseDurationSelection
              phaseId={props.phaseId}
              durationInputRef={phasesHook.refs.durationInput}
            />
          </Box>
          {props.wideLayout ? (
            <Box padding="0 16px">
              <PricingTableExpanded phaseId={props.phaseId} />
            </Box>
          ) : (
            <PricingTableCompact phaseId={props.phaseId} />
          )}
          {phasesHook.showProductSearch && (
            <Box marginBottom="16px" padding="0 16px">
              <SearchForProduct
                onSelection={() => phasesHook.setShowAddProductSearch(false)}
                phaseId={props.phaseId}
                onBlur={() => phasesHook.setShowAddProductSearch(false)}
              />
            </Box>
          )}
          {Boolean(phasesHook.validationErrors.length) && (
            <Flex
              data-testid="phase.validationError"
              marginBottom="16px"
              color={RedRed60}
              {...Primary13Regular}
              justifyContent="center"
            >
              {phasesHook.validationErrors[0].message}
            </Flex>
          )}
          <Box padding="0 16px">
            <PhaseActions phasesHook={phasesHook} />
          </Box>
        </Box>
      )}
    </PhaseContainer>
  )
}
